import Button, { ButtonTypes } from "../../../../../components/Button";
import {
  DateFormat,
  formatCurrency,
  formatDate,
  formatSignedCurrency,
  formatSignedPercent,
} from "../../../../../utils";
import {
  FigureMuted,
  Hero,
  HeroFigure,
  HeroLabel,
  HeroValue,
  Ledger,
  LedgerFigure,
  LedgerNote,
  LedgerRow,
  LedgerTerm,
  PeriodLabel,
  PreviewWrapper,
  Section,
  SectionTitle,
  SubSection,
  SubSectionTitle,
} from "./styles";
import { InvestmentIncomeSection } from "./InvestmentIncomeSection";
import { FigureTone, ReportPreviewProps } from "./types";

const VALUATION_UNAVAILABLE_NOTE =
  "Market gain/loss and total return need full valuation coverage for every holding, so they're not shown for this period.";

// Delta figures read green up, red down, neutral at zero (or when unavailable). The +/- sign in the
// formatted value carries the same meaning, so colour is never the sole signal.
const toneForValue = (value: number | null): FigureTone => {
  if (value === null || value === 0) {
    return "neutral";
  }
  if (value > 0) {
    return "positive";
  }
  return "negative";
};

export const ReportPreview: React.FC<ReportPreviewProps> = ({
  report,
  isDownloading,
  onDownload,
}) => {
  const { period, summary, netWorth, topExpenseCategories } = report;

  const periodLabel = `${formatDate(period.startDate, DateFormat.DMY_SPACE)} – ${formatDate(
    period.endDate,
    DateFormat.DMY_SPACE,
  )} (${period.days} days)`;

  const hasCategories = topExpenseCategories.length > 0;
  const hasCashbackPaid = summary.cashbackPaid !== 0;

  // Only the tax-year response carries returnsActivity; its presence discriminates the report kind.
  const returnsActivity =
    "returnsActivity" in report ? report.returnsActivity : null;
  const investmentIncome =
    "investmentIncome" in report ? report.investmentIncome : null;
  const investments = returnsActivity?.investments;
  const showInvestmentReturns = investments?.hasSufficientValuation ?? false;
  // A gain is only rendered with sufficient valuation and a non-null value; the two fields are
  // independent, so a null percentage never suppresses a valid gain (and vice versa).
  const marketGainLossLabel =
    showInvestmentReturns && investments && investments.marketGainLoss !== null
      ? formatSignedCurrency(investments.marketGainLoss)
      : null;

  return (
    <PreviewWrapper aria-label="Report preview">
      <PeriodLabel data-testid="report-period">{periodLabel}</PeriodLabel>

      <Hero>
        <HeroFigure>
          <HeroLabel>Balance</HeroLabel>
          <HeroValue
            $tone={toneForValue(summary.balance)}
            data-testid="report-balance"
          >
            {formatSignedCurrency(summary.balance)}
          </HeroValue>
        </HeroFigure>
        <HeroFigure>
          <HeroLabel>Net worth change</HeroLabel>
          <HeroValue
            $tone={toneForValue(netWorth.change)}
            data-testid="report-networth-change"
          >
            {formatSignedCurrency(netWorth.change)}
          </HeroValue>
        </HeroFigure>
      </Hero>

      <Section>
        <SectionTitle>Cashflow</SectionTitle>
        <Ledger>
          <LedgerRow>
            <LedgerTerm>Income</LedgerTerm>
            <LedgerFigure data-testid="report-income">
              {formatCurrency(summary.income)}
            </LedgerFigure>
          </LedgerRow>
          {hasCashbackPaid && (
            <LedgerRow>
              <LedgerTerm>Of which cashback</LedgerTerm>
              <LedgerFigure data-testid="report-cashback">
                {formatCurrency(summary.cashbackPaid)}
              </LedgerFigure>
            </LedgerRow>
          )}
          <LedgerRow>
            <LedgerTerm>Expenses</LedgerTerm>
            <LedgerFigure data-testid="report-expenses">
              {formatCurrency(summary.expenses)}
            </LedgerFigure>
          </LedgerRow>
          <LedgerRow>
            <LedgerTerm>Tax withheld on income</LedgerTerm>
            <LedgerFigure data-testid="report-tax-withheld-on-income">
              {formatCurrency(summary.taxWithheldOnIncome)}
            </LedgerFigure>
          </LedgerRow>
        </Ledger>
      </Section>

      <Section>
        <SectionTitle>Net worth</SectionTitle>
        <Ledger>
          <LedgerRow>
            <LedgerTerm>Opening</LedgerTerm>
            <LedgerFigure>{formatCurrency(netWorth.opening)}</LedgerFigure>
          </LedgerRow>
          <LedgerRow>
            <LedgerTerm>Closing</LedgerTerm>
            <LedgerFigure>{formatCurrency(netWorth.closing)}</LedgerFigure>
          </LedgerRow>
        </Ledger>
      </Section>

      {returnsActivity && (
        <Section data-testid="report-returns-activity">
          <SectionTitle>Savings and investments</SectionTitle>

          <SubSection>
            <SubSectionTitle>Savings</SubSectionTitle>
            <Ledger>
              <LedgerRow>
                <LedgerTerm>Gross interest</LedgerTerm>
                <LedgerFigure data-testid="report-savings-gross-interest">
                  {formatCurrency(returnsActivity.savings.grossInterest)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Tax withheld</LedgerTerm>
                <LedgerFigure data-testid="report-savings-tax">
                  {formatCurrency(returnsActivity.savings.taxWithheld)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Net received</LedgerTerm>
                <LedgerFigure data-testid="report-savings-interest">
                  {formatCurrency(returnsActivity.savings.interestEarned)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Cashback</LedgerTerm>
                <LedgerFigure data-testid="report-savings-cashback">
                  {formatCurrency(returnsActivity.savings.cashback)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Contributions</LedgerTerm>
                <LedgerFigure data-testid="report-savings-contributions">
                  {formatCurrency(returnsActivity.savings.contributions)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Withdrawals</LedgerTerm>
                <LedgerFigure data-testid="report-savings-withdrawals">
                  {formatCurrency(returnsActivity.savings.withdrawals)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Opening value</LedgerTerm>
                <LedgerFigure data-testid="report-savings-opening">
                  {formatCurrency(returnsActivity.savings.openingValue)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Closing value</LedgerTerm>
                <LedgerFigure data-testid="report-savings-closing">
                  {formatCurrency(returnsActivity.savings.closingValue)}
                </LedgerFigure>
              </LedgerRow>
            </Ledger>
          </SubSection>

          <SubSection>
            <SubSectionTitle>Investments</SubSectionTitle>
            <Ledger>
              <LedgerRow>
                <LedgerTerm>Contributions</LedgerTerm>
                <LedgerFigure data-testid="report-investments-contributions">
                  {formatCurrency(returnsActivity.investments.contributions)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Withdrawals / proceeds</LedgerTerm>
                <LedgerFigure data-testid="report-investments-withdrawals">
                  {formatCurrency(returnsActivity.investments.withdrawals)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Tax withheld</LedgerTerm>
                <LedgerFigure data-testid="report-investments-tax-withheld">
                  {formatCurrency(returnsActivity.investments.taxWithheld)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Opening market value</LedgerTerm>
                <LedgerFigure data-testid="report-investments-opening">
                  {formatCurrency(returnsActivity.investments.openingValue)}
                </LedgerFigure>
              </LedgerRow>
              <LedgerRow>
                <LedgerTerm>Closing market value</LedgerTerm>
                <LedgerFigure data-testid="report-investments-closing">
                  {formatCurrency(returnsActivity.investments.closingValue)}
                </LedgerFigure>
              </LedgerRow>
              {marketGainLossLabel !== null && (
                <LedgerRow>
                  <LedgerTerm>Market gain / loss</LedgerTerm>
                  <LedgerFigure
                    $tone={toneForValue(
                      returnsActivity.investments.marketGainLoss,
                    )}
                    data-testid="report-investments-market-gain"
                  >
                    {marketGainLossLabel}
                  </LedgerFigure>
                </LedgerRow>
              )}
              {showInvestmentReturns && (
                <LedgerRow>
                  <LedgerTerm>Total return</LedgerTerm>
                  <LedgerFigure
                    $tone={toneForValue(
                      returnsActivity.investments.totalReturnPct,
                    )}
                    data-testid="report-investments-return-pct"
                  >
                    {formatSignedPercent(
                      returnsActivity.investments.totalReturnPct,
                    )}
                  </LedgerFigure>
                </LedgerRow>
              )}
            </Ledger>
            {!showInvestmentReturns && (
              <LedgerNote data-testid="report-returns-unavailable">
                {VALUATION_UNAVAILABLE_NOTE}
              </LedgerNote>
            )}
          </SubSection>
        </Section>
      )}

      {investmentIncome && (
        <InvestmentIncomeSection income={investmentIncome} />
      )}

      {hasCategories && (
        <Section>
          <SectionTitle>Top expense categories</SectionTitle>
          <Ledger>
            {topExpenseCategories.map((category) => (
              <LedgerRow key={category.name}>
                <LedgerTerm>{category.name}</LedgerTerm>
                <LedgerFigure>
                  {formatCurrency(category.amount)}{" "}
                  <FigureMuted>({category.percentage}%)</FigureMuted>
                </LedgerFigure>
              </LedgerRow>
            ))}
          </Ledger>
        </Section>
      )}

      <div>
        <Button
          type={ButtonTypes.primary}
          onClick={onDownload}
          disabled={isDownloading}
          isLoading={isDownloading}
          data-testid="download-pdf-button"
        >
          Download PDF
        </Button>
      </div>
    </PreviewWrapper>
  );
};
